import { useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import type { Vehicle } from '../../types/vehicle';
import { hasLivePosition } from '../../types/vehicle';
import type { TripRoute } from '../../types/trip';
import { VEHICLE_STATUS_COLORS, VEHICLE_STATUS_LABELS, MAP_CENTER, MAP_ZOOM } from '../../utils/constants';
import { formatRelative } from '../../utils/formatters';
import { RouteHistoryLayers } from './RouteHistoryLayers';
import { MapTiles, MapStyleSelector, MapResizeHandler, type MapStyleMode } from './mapBase';
import type { InterpolatedPosition, RouteStop } from '../../utils/vehicleHistory';

// Fix Leaflet default icon issue with Vite
delete (L.Icon.Default.prototype as L.Icon.Default & { _getIconUrl?: string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function createVehicleIcon(status: Vehicle['status'], isSelected: boolean) {
  const color = VEHICLE_STATUS_COLORS[status];
  const size = isSelected ? 18 : 14;
  const pulse = status === 'critical_event';

  return L.divIcon({
    className: '',
    html: `
      <div style="position:relative;width:${size + 10}px;height:${size + 10}px;">
        ${pulse ? `<div style="
          position:absolute;inset:0;border-radius:50%;
          background:${color};opacity:0.4;
          animation:map-pulse 1.8s ease-in-out infinite;
        "></div>` : ''}
        <div style="
          position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%);
          width:${size}px;height:${size}px;border-radius:50%;
          background:${color};
          border:${isSelected ? '3px solid #FFFFFF' : '2px solid rgba(255,255,255,0.85)'};
          box-shadow:0 0 ${isSelected ? '14px' : '8px'} ${color};
        "></div>
      </div>`,
    iconSize: [size + 10, size + 10],
    iconAnchor: [(size + 10) / 2, (size + 10) / 2],
  });
}

/**
 * `leaflet.markercluster` no publica tipos y `react-leaflet-cluster` los declara
 * contra un `L.MarkerClusterGroupOptions` que no existe en este arbol
 * (`skipLibCheck` lo tapa). Declaramos lo unico que este callback consume.
 */
interface MarkerClusterLike {
  getChildCount(): number;
}

// Modern cluster icon creation
const createClusterCustomIcon = function (cluster: MarkerClusterLike) {
  const count = cluster.getChildCount();
  let size = 36;
  if (count > 10) size = 42;
  if (count > 50) size = 48;

  return L.divIcon({
    html: `
      <div style="
        background: rgba(15, 23, 42, 0.85);
        color: white;
        border: 2px solid var(--accent);
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        backdrop-filter: blur(4px);
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

function FlyToSelected({ vehicle }: { vehicle: Vehicle | null }) {
  const map = useMap();
  useEffect(() => {
    // Sin posición real (centinela 0,0) no se vuela: ir a Null Island muestra
    // el tile gris "Map data not yet available" de Esri (equipo sin señal aún).
    if (vehicle && hasLivePosition(vehicle.position)) {
      map.flyTo([vehicle.position.lat, vehicle.position.lng], 14, { duration: 1.2 });
    }
  }, [vehicle, map]);
  return null;
}

interface MapViewProps {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  onVehicleClick?: (vehicle: Vehicle) => void;
  /** FE-25 — recorrido histórico a dibujar como capa adicional, sin ocultar los vehículos en vivo. */
  historyRoute?: TripRoute | null;
  /** FE-26 — posición interpolada de la reproducción animada del histórico. */
  playbackPosition?: InterpolatedPosition | null;
  /** FE-26 — paradas detectadas del histórico actual. */
  stops?: RouteStop[];
  /** FE-27 — el mapa acompaña al vehículo (modo "Ubicar" de `HistoryPlaybackPanel`). */
  following?: boolean;
  /** FE-27 — el usuario arrastró el mapa: se apaga el seguimiento. */
  onStopFollowing?: () => void;
}

export function MapView({ vehicles, selectedVehicle, onVehicleClick, historyRoute, playbackPosition, stops, following, onStopFollowing }: MapViewProps) {
  const [mapStyle, setMapStyle] = useState<MapStyleMode>('satellite'); // Default to Satellite HD

  return (
    <div className="map-container" style={{ width: '100%', height: '100%', minHeight: '350px', position: 'relative' }}>
      
      {/* Map style selector trigger floating on top-right */}
      <MapStyleSelector mapStyle={mapStyle} onChange={setMapStyle} />

      <MapContainer
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        style={{ width: '100%', height: '100%', minHeight: '350px' }}
        zoomControl={false}
      >
        <MapTiles mapStyle={mapStyle} />

        <MapResizeHandler />
        <FlyToSelected vehicle={selectedVehicle} />

        <RouteHistoryLayers
          route={historyRoute}
          playbackPosition={playbackPosition}
          stops={stops}
          following={following}
          onStopFollowing={onStopFollowing}
        />

        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
        >
          {vehicles.filter(v => hasLivePosition(v.position)).map(vehicle => (
            <Marker
              key={vehicle.id}
              position={[vehicle.position.lat, vehicle.position.lng]}
              icon={createVehicleIcon(vehicle.status, selectedVehicle?.id === vehicle.id)}
              eventHandlers={{ click: () => onVehicleClick?.(vehicle) }}
            >
              <Popup className="map-popup">
                <div className="map-popup__content">
                  <div className="map-popup__plate">{vehicle.plate}</div>
                  <div className="map-popup__brand">{vehicle.brand} {vehicle.model} {vehicle.year}</div>
                  {vehicle.driverName && (
                    <div className="map-popup__driver">👤 {vehicle.driverName}</div>
                  )}
                  <div className="map-popup__status" style={{ color: VEHICLE_STATUS_COLORS[vehicle.status] }}>
                    ● {VEHICLE_STATUS_LABELS[vehicle.status]}
                  </div>
                  {vehicle.position.speed !== undefined && vehicle.status === 'in_route' && (
                    <div className="map-popup__speed">{Math.round(vehicle.position.speed)} km/h</div>
                  )}
                  <div className="map-popup__time">Hace {formatRelative(vehicle.lastSeen)}</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Legend */}
      <div className="map-legend">
        {(Object.entries(VEHICLE_STATUS_COLORS) as [Vehicle['status'], string][]).map(([status, color]) => (
          <div key={status} className="map-legend__item">
            <span className="map-legend__dot" style={{ background: color }} />
            <span className="map-legend__label">{VEHICLE_STATUS_LABELS[status]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

